import type { Hierarchy } from "@/api/flash.service";
import type { CatFlatRow, SortKey, TableRow } from "./types";

export function toRow(item: Hierarchy): TableRow {
  return {
    key: `${item.cod_empresa}-${item.nomeempresaapp}`,
    nome: item.nomeempresaapp,
    atual: item.mes_atual,
    meta: item.meta_mes,
    ant: item.mes_anterior,
    ano: item.ano_anterior,
    difMeta: Number(item.variacao_atual),
    difAnt: Number(item.variacao_mes_anterior),
    difAno: Number(item.variacao_ano_anterior),
  };
}

export function sortRows(rows: TableRow[], key: SortKey, asc: boolean): TableRow[] {
  return [...rows].sort((a, b) => {
    const va = a[key] as number | string;
    const vb = b[key] as number | string;
    if (typeof va === "string") return asc ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
    return asc ? (va as number) - (vb as number) : (vb as number) - (va as number);
  });
}

export function sortHierarchy(items: Hierarchy[], key: SortKey, asc: boolean): Hierarchy[] {
  return [...items].sort((a, b) => {
    let va: number | string;
    let vb: number | string;
    switch (key) {
      case "nome": va = a.nomeempresaapp; vb = b.nomeempresaapp; break;
      case "atual": va = a.mes_atual; vb = b.mes_atual; break;
      case "meta": va = a.meta_mes; vb = b.meta_mes; break;
      case "ant": va = a.mes_anterior; vb = b.mes_anterior; break;
      case "ano": va = a.ano_anterior; vb = b.ano_anterior; break;
      case "difMeta": va = Number(a.variacao_atual); vb = Number(b.variacao_atual); break;
      case "difAnt": va = Number(a.variacao_mes_anterior); vb = Number(b.variacao_mes_anterior); break;
      default: va = Number(a.variacao_ano_anterior); vb = Number(b.variacao_ano_anterior); break;
    }
    if (typeof va === "string") return asc ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
    return asc ? (va as number) - (vb as number) : (vb as number) - (va as number);
  });
}

export function buildCatFlatRows(
  cats: Hierarchy[],
  expandedRows: Set<string>,
  subData: Record<string, Hierarchy[]>,
  loadingRows: Record<string, boolean>,
  sk: SortKey,
  sa: boolean,
  depth = 0,
  parentKey = "",
  parentCatCode: string | null = null,
): CatFlatRow[] {
  const rows: CatFlatRow[] = [];
  sortHierarchy(cats, sk, sa).forEach((cat) => {
    const key = parentKey ? `${parentKey}-${cat.cod_empresa}` : `cat-${cat.cod_empresa}`;
    const isExpanded = expandedRows.has(key);
    const isLoadingChildren = !!loadingRows[cat.cod_empresa];
    const canExpand = cat.canalapp !== "PRODUTO";

    rows.push({ item: cat, key, depth, isExpanded, isLoadingChildren, canExpand, parentCatCode });

    if (isExpanded && !isLoadingChildren) {
      const children = (subData[cat.cod_empresa] ?? []).filter((c) => c.nomeempresaapp);
      if (children.length > 0)
        rows.push(...buildCatFlatRows(children, expandedRows, subData, loadingRows, sk, sa, depth + 1, key, cat.cod_empresa));
    }
  });
  return rows;
}

export function sumToTotRow(rows: TableRow[]): TableRow {
  const atual = rows.reduce((s, r) => s + r.atual, 0);
  const meta = rows.reduce((s, r) => s + r.meta, 0);
  const ant = rows.reduce((s, r) => s + r.ant, 0);
  const ano = rows.reduce((s, r) => s + r.ano, 0);
  return {
    key: "total",
    nome: "Total",
    atual, meta, ant, ano,
    difMeta: meta > 0 ? ((atual - meta) / meta) * 100 : 0,
    difAnt: ant > 0 ? ((atual - ant) / ant) * 100 : 0,
    difAno: ano > 0 ? ((atual - ano) / ano) * 100 : 0,
  };
}
